// Every number Emissions displays goes through one of these formatters, so that rounding and separators are
// consistent from screen to screen. They format with the viewer's locale (i.e. `5,220,000.5` in the US,
// `5.220.000,5` in Germany).
//
// Round only for display. Calculations, totals, and exports should always use the unrounded values.
//
// There are two tiers:
// - Summary (mass totals, limit utilization): a fixed number of decimal places so columns line up.
// - Detail (calculation steps, formula inputs, limits, and anything else in arbitrary units): enough precision for a
//   user to check our arithmetic.

import { emissionsMassFromPounds, emissionsMassUnitList, emissionsMassUnits } from './emissions-mass-units.ts';
import type { EmissionsMassUnitID } from '../../../cross-app-shared/types/emissions-mass-units.ts';

const EMPTY = '—';

// Used when a summary value is too small to show at its fixed number of decimal places, so that a small but
// non-zero emission never displays as zero.
const smallValueFormat = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 2 });

const detailLargeFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });
const detailSmallFormat = new Intl.NumberFormat(undefined, { maximumSignificantDigits: 4 });

const percentFormat = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const percentAxisFormat = new Intl.NumberFormat(undefined, { style: 'percent', maximumFractionDigits: 1 });
const concentrationDecimalPlaces = 2;
const concentrationFormat = new Intl.NumberFormat(undefined, {
  style: 'percent',
  minimumFractionDigits: concentrationDecimalPlaces,
  maximumFractionDigits: concentrationDecimalPlaces,
});
const concentrationSmallFormat = new Intl.NumberFormat(undefined, { style: 'percent', maximumSignificantDigits: 2 });

function fixedFormat(decimalPlaces: number): Intl.NumberFormat {
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

const massFormats = Object.fromEntries(
  emissionsMassUnitList.map((unit) => [unit.id, fixedFormat(unit.decimalPlaces)]),
) as Record<EmissionsMassUnitID, Intl.NumberFormat>;

function isDisplayable(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Summary tier: an amount of mass, in a fixed number of decimal places for the unit. */
export function emissionsNumberFormatMass(value: number | null | undefined, unit: EmissionsMassUnitID): string {
  if (!isDisplayable(value)) return EMPTY;
  if (value === 0) return '0';
  if (Math.abs(value) < 10 ** -emissionsMassUnits[unit].decimalPlaces) return smallValueFormat.format(value);
  return massFormats[unit].format(value);
}

/** Summary tier: an amount of mass tracked in pounds, shown in the given unit. */
export function emissionsNumberFormatMassFromPounds(
  pounds: number | null | undefined,
  unit: EmissionsMassUnitID,
): string {
  return emissionsNumberFormatMass(isDisplayable(pounds) ? emissionsMassFromPounds(pounds, unit) : pounds, unit);
}

/** Detail tier: a number in arbitrary units, precise enough to check a calculation by hand. */
export function emissionsNumberFormat(value: number | null | undefined): string {
  if (!isDisplayable(value)) return EMPTY;
  if (Math.abs(value) >= 1) return detailLargeFormat.format(value);
  return detailSmallFormat.format(value);
}

/** Detail tier, followed by units (i.e. `1,234.5 lb/hr`). */
export function emissionsNumberFormatWithUnits(value: number | null | undefined, units: string): string {
  const formatted = emissionsNumberFormat(value);
  return units && formatted !== EMPTY ? `${formatted} ${units}` : formatted;
}

/** A fraction (0.5) shown as a percentage (50.0%). */
export function emissionsNumberFormatPercent(fraction: number | null | undefined): string {
  if (!isDisplayable(fraction)) return EMPTY;
  return percentFormat.format(fraction);
}

/** A fraction shown as a percentage without trailing zeros, for chart axis ticks (0.5 -> 50%). */
export function emissionsNumberFormatPercentAxisTick(fraction: number): string {
  return percentAxisFormat.format(fraction);
}

/** A chemical's concentration in a product, as a fraction (0.1234), shown as a percentage (12.34%). */
export function emissionsNumberFormatConcentration(fraction: number | null | undefined): string {
  if (!isDisplayable(fraction)) return EMPTY;
  if (fraction !== 0 && Math.abs(fraction * 100) < 10 ** -concentrationDecimalPlaces) {
    return concentrationSmallFormat.format(fraction);
  }
  return concentrationFormat.format(fraction);
}

/**
 * Formula values arrive from the substitutor as strings, i.e. `43.5 MMcf` or `0.30000000000000004`. Format the number
 * so formula inputs display the same way as everything else, and leave anything else (quoted strings) alone.
 */
export function emissionsNumberFormatSubstitutedValue(value: string): string {
  const spaceIndex = value.indexOf(' ');
  const numberPart = spaceIndex === -1 ? value : value.slice(0, spaceIndex);
  const unitsPart = spaceIndex === -1 ? '' : value.slice(spaceIndex + 1);
  if (numberPart.trim() === '') return value;
  const parsed = Number(numberPart);
  if (!Number.isFinite(parsed)) return value;
  return emissionsNumberFormatWithUnits(parsed, unitsPart);
}
